import { useState } from 'react';

const QuoteSection = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: '',
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (formData.message.length > 500) {
      setError('Het bericht mag niet langer zijn dan 500 tekens.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const body = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => body.append(key, value));
      const res = await fetch('https://readdy.ai/api/form/d6mrfj5v117fnkj2h9eg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      if (res.ok) {
        setSubmitted(true);
        setFormData({ name: '', email: '', phone: '', service: '', message: '' });
      } else {
        setError('Fout tijdens het verzenden. Probeer het later opnieuw.');
      }
    } catch {
      setError('Fout tijdens het verzenden. Probeer het later opnieuw.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="w-full py-24 px-6 lg:px-12" style={{ backgroundColor: '#f9f7f4' }}>
      <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
        {/* Left side */}
        <div>
          <span className="text-xs font-semibold tracking-widest uppercase text-amber-600 mb-4 block">
            Gratis Offerte
          </span>
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 leading-tight mb-6" style={{ fontFamily: 'serif' }}>
            Vraag een<br />
            <span className="text-amber-600">Offerte aan</span>
          </h2>
          <p className="text-gray-500 text-base leading-relaxed mb-10">
            Vertel ons over uw renovatieproject. We reageren binnen 24 uur met een persoonlijke en vrijblijvende schatting.
          </p>

          <div className="space-y-6">
            {[
              { icon: 'ri-time-line', title: 'Reactie binnen 24 uur', desc: 'We nemen snel contact met u op om uw project te bespreken.' },
              { icon: 'ri-money-euro-circle-line', title: 'Gratis offerte', desc: 'Geen verborgen kosten, geen verplichtingen.' },
              { icon: 'ri-shield-check-line', title: 'Kwaliteit gegarandeerd', desc: 'We werken met eersteklas materialen en ervaren vaklieden.' },
            ].map((item) => (
              <div key={item.title} className="flex items-start gap-4">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-amber-100 shrink-0">
                  <i className={`${item.icon} text-amber-600 text-lg`}></i>
                </div>
                <div>
                  <p className="font-semibold text-gray-800 text-sm">{item.title}</p>
                  <p className="text-gray-500 text-sm">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Right side - Form */}
        <div className="bg-white rounded-2xl shadow-sm p-8 lg:p-10">
          {submitted ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <div className="w-16 h-16 flex items-center justify-center rounded-full bg-green-100 mb-4">
                <i className="ri-check-line text-green-600 text-3xl"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Aanvraag verzonden!</h3>
              <p className="text-gray-500 text-sm">Bedankt voor uw bericht. We reageren binnen 24 uur.</p>
              <button
                onClick={() => setSubmitted(false)}
                className="mt-6 text-sm text-amber-600 underline cursor-pointer"
              >
                Stuur een nieuwe aanvraag
              </button>
            </div>
          ) : (
            <form
              id="quote-form-homepage"
              data-readdy-form
              onSubmit={handleSubmit}
              className="space-y-5"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">
                    Naam en Achternaam *
                  </label>
                  <input
                    type="text"
                    name="name"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Jan Janssen"
                    className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:border-amber-500 transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">
                    E-mail *
                  </label>
                  <input
                    type="email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="jan@email.com"
                    className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:border-amber-500 transition-colors"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">
                    Telefoon
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="+32 000 000 000"
                    className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:border-amber-500 transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">
                    Type dienst *
                  </label>
                  <select
                    name="service"
                    required
                    value={formData.service}
                    onChange={handleChange}
                    className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm text-gray-800 focus:outline-none focus:border-amber-500 transition-colors cursor-pointer"
                  >
                    <option value="">Selecteer...</option>
                    <option value="Volledige renovatie">Volledige renovatie</option>
                    <option value="Badkamer">Badkamer</option>
                    <option value="Keuken">Keuken</option>
                    <option value="Vloerwerk">Vloerwerk</option>
                    <option value="Schilderwerk en decoratie">Schilderwerk en decoratie</option>
                    <option value="Overige">Overige</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">
                  Beschrijf uw project *
                </label>
                <textarea
                  name="message"
                  required
                  value={formData.message}
                  onChange={handleChange}
                  maxLength={500}
                  rows={4}
                  placeholder="Beschrijf kort het werk dat u wilt laten uitvoeren..."
                  className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:border-amber-500 transition-colors resize-none"
                />
                <p className="text-xs text-gray-400 text-right mt-1">{formData.message.length}/500</p>
              </div>

              {error && (
                <p className="text-red-500 text-sm">{error}</p>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-gray-900 hover:bg-amber-600 text-white font-semibold py-3.5 rounded-lg text-sm transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60"
              >
                {loading ? 'Bezig met verzenden...' : 'Offerte aanvragen'}
              </button>

              <p className="text-xs text-gray-400 text-center">
                Uw gegevens zijn veilig. We delen ze nooit met derden.
              </p>
            </form>
          )}
        </div>
      </div>
    </section>
  );
};

export default QuoteSection;
